import { create } from 'zustand';
import {
  MasterAssembly,
  CatalogFigure,
  SvgFile,
  BomItem,
} from '../models/MasterAssembly';
import { masterAssemblyApi } from '../apiServices/masterassembly';
import { useUserStore } from './userStore';

// Assembly status interface
export interface AssemblyStatus {
  statusId: number;
  statusType: string;
}

export interface LocalizedTranslation {
  /** Human‑readable language identifier (e.g. "English", "Spanish"). */
  language: string;
  /** The translated text corresponding to the given language. */
  value: string;
}

export interface PartTranslation {
  /** The part number this translation belongs to. */
  partNumber: string;
  /** All language translations for this part. */
  translations: LocalizedTranslation[];
}

export interface FigureTranslationData {
  /** Optional translations for the figure title in multiple languages. */
  figureTitleTranslations: LocalizedTranslation[];
  /** Optional translations for part descriptions per language. */
  partTranslations: PartTranslation[];
  /** The file name of the uploaded translation document. */
  fileName?: string;
}


interface AuthoringState {
  // Form fields
  oemName: string;
  partNumber: string;
  partDescription: string;
  partRevision: string;
  mainSvgPath: string;
  mainSvgFileName: string;
  svgId: number;
  workspaceStatusId: number;

  // Cards derived from CatalogFigure
  cards: (CatalogFigure & { translationData?: { 
    figureTitleTranslations: LocalizedTranslation[], 
    partTranslations: PartTranslation[], 
    fileName?: string 
  } })[];
  showCards: boolean;
  showArrows: boolean;

  // MasterAssembly from backend
  masterAssembly: MasterAssembly | null;

  // Part statuses
  partStatuses: AssemblyStatus[];
  setPartStatuses: (statuses: AssemblyStatus[]) => void;
  getStatusById: (id: number) => AssemblyStatus | undefined;

  // Setters
  setOEMName: (val: string) => void;
  setPartNumber: (val: string) => void;
  setPartDescription: (val: string) => void;
  setPartRevision: (val: string) => void;
  setShowCards: (val: boolean) => void;
  setShowArrows: (val: boolean) => void;

  // Card manipulation
  addInitialCard: () => void;
  addCard: () => void;
  deleteCard: (index: number) => void;
  resetCards: () => void;
  setMainSvg: (svg: { svgId: number; folderPath: string; fileName: string }) => void;

  // SVG handling
  addIllustration: (cardIndex: number, file: File) => void;
  pushIllustrations: (cardIndex: number, svgs: SvgFile[]) => void;

  // BOM handling
  appendBomItems: (cardIndex: number, items: BomItem[]) => void;
  replaceBomItems: (cardIndex: number, items: BomItem[]) => void;

  deleteIllustration: (cardIndex: number, svgIndex: number) => void;

  // Backend helpers
  setMasterAssembly: (data: MasterAssembly) => void;
  buildAddPayload: () => MasterAssembly;
    fetchAndSetPartStatuses: (oem: string) => Promise<void>;

}

export const useAuthoringStore = create<AuthoringState>((set, get) => ({
  oemName: '',
  partNumber: '',
  partDescription: '',
  partRevision: '',
  mainSvgPath: '',
  mainSvgFileName: '',
  svgId: 0,
  workspaceStatusId: 0,

  cards: [],
  showCards: false,
  showArrows: false,

  masterAssembly: null,

  partStatuses: [],
  setPartStatuses: (statuses: AssemblyStatus[]) => set({ partStatuses: statuses }),
  getStatusById: (id: number) => get().partStatuses.find((s) => s.statusId === id),

  // Field setters
  setOEMName: (val) => set({ oemName: val }),
  setPartNumber: (val) => set({ partNumber: val }),
  setPartDescription: (val) => set({ partDescription: val }),
  setPartRevision: (val) => set({ partRevision: val }),
  setShowCards: (val) => set({ showCards: val }),
  setShowArrows: (val) => set({ showArrows: val }),

  // Inside your useAuthoringStore
fetchAndSetPartStatuses: async (oem: string) => {
  try {
    console.log("Oemvale", oem)
    const statuses = await masterAssemblyApi.getAllAssemblyStatuses(oem);
    set({ partStatuses: statuses });
    console.log('Assembly statuses updated:', statuses);
  } catch (error) {
    console.error('Failed to fetch assembly statuses:', error);
  }
},


  // Load GET response into store
  setMasterAssembly: (data) => {
    set({
      masterAssembly: data,
      oemName: 'OEM4',
      partNumber: data?.componentPartNumber || '',
      partDescription: data?.componentDescription || '',
      partRevision: data?.currentVersionId?.toString() || '',
      svgId: data?.mainSvg?.svgId ?? 0,
      mainSvgPath: data?.mainSvg?.folderPath || '',
      mainSvgFileName: data?.mainSvg?.fileName || '',
      workspaceStatusId: data?.workspaceAssembly?.statusId ?? 0,
      cards: (data?.catalog || []).map((c) => ({
        ...c,
        svgFiles: Array.isArray(c.svgFiles) ? c.svgFiles : [],
        bomJson: Array.isArray(c.bomJson) ? c.bomJson : [],
      })),
      showCards: Array.isArray(data?.catalog) && data.catalog.length > 0,
    });
  },

  setMainSvg: (svg: { svgId: number; folderPath: string; fileName: string }) =>
    set({
      svgId: svg.svgId,
      mainSvgPath: svg.folderPath,
      mainSvgFileName: svg.fileName,
    }),

  // Build POST payload
  buildAddPayload: () => {
    const { oemName, partNumber, partDescription, cards, partRevision, svgId, mainSvgPath, mainSvgFileName, workspaceStatusId } = get();
    const now = new Date().toISOString();
    const user = useUserStore.getState().user;
    const actor = user?.username?.trim() || user?.emailId?.trim() || oemName || 'system';

    return {
      masterAssemblyId: 0,
      createdBy: actor,
      createdOn: now,
      modifiedBy: actor,
      modifiedOn: now,
      workspaceAssemblyId: 0,
      finalAssemblyId: 0,
      currentVersionId: Number(partRevision) || 0,
      isFinalized: true,
      isActive: true,
      componentPartNumber: partNumber,
      componentDescription: partDescription,
      mainSvg: {
        svgId: svgId || 0,
        folderPath: mainSvgPath || '',
        fileName: mainSvgFileName || '',
      },
      finalAssembly: { assemblyId: 0 },
      workspaceAssembly: {
        workspaceAssemblyId: 0,
        versionNumber: 1,
        remarks: '',
        createdBy: actor,
        createdOn: now,
        modifiedBy: actor,
        modifiedOn: now,
        statusId: workspaceStatusId,
      },
    catalog: cards.map((f) => {
  // 👇 NEW: flatten bomFiles into bomJson
  const bomJson = f.bomFiles?.length
    ? f.bomFiles.flatMap((file) => file.items || [])
    : (f.bomJson || []);

  return {
    figId: f.figId,
    figOrder: f.figOrder,
    figNumber: f.figNumber,
    figTitle: f.figTitle,
    bomJson,                   // ✅ now includes bomFiles content
    svgFiles: f.svgFiles || [],
  };
}),

    };
  },

  // Cards
  addInitialCard: () =>
    set(() => {
      const newCard: CatalogFigure = {
        figId: 0,
        figOrder: 1,
        figNumber: 'Fig 1',
        figTitle: '',
        bomJson: [],
        svgFiles: [],
      };
      return { cards: [newCard], showCards: true };
    }),

  addCard: () =>
    set((state) => {
      const index = state.cards.length + 1;
      const newCard: CatalogFigure = {
        figId: 0,
        figOrder: index,
        figNumber: `Fig ${index}`,
        figTitle: '',
        bomJson: [],
        svgFiles: [],
      };
      return { cards: [...state.cards, newCard] };
    }),

  deleteCard: (index) =>
    set((state) => {
      const cards = state.cards.filter((_, i) => i !== index);
      return { cards };
    }),

  resetCards: () => set({ cards: [], showCards: false }),

  // SVG handling
  addIllustration: (cardIndex, file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const state = get();
      const cards = [...state.cards];
      const svg: SvgFile = {
        svgId: 0,
        folderPath: '',
        fileName: file.name,
      };
      cards[cardIndex].svgFiles.push(svg);
      set({ cards });
    };
    reader.readAsText(file);
  },

  pushIllustrations: (cardIndex, svgs) =>
    set((state) => {
      const cards = [...state.cards];
      cards[cardIndex].svgFiles = [...(cards[cardIndex].svgFiles || []), ...svgs];
      return { cards };
    }),

  // BOM handling
  appendBomItems: (cardIndex, items) =>
    set((state) => {
      const cards = [...state.cards];
      cards[cardIndex].bomJson = [...(cards[cardIndex].bomJson || []), ...items];
      return { cards };
    }),

  replaceBomItems: (cardIndex, items) =>
    set((state) => {
      const cards = [...state.cards];
      cards[cardIndex].bomJson = items;
      return { cards };
    }),

  deleteIllustration: (cardIndex, svgIndex) =>
    set((state) => {
      const cards = [...state.cards];
      cards[cardIndex].svgFiles.splice(svgIndex, 1);
      return { cards };
    }),
}));
